import { createStyles, Input, makeStyles, Theme } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import React, { useState } from 'react';

import { ReactComponent as SearchIconSvg } from '../../../../assets/search.svg';
import { useDomainPlexesQuery } from '../../../../graphql/gen/types';
import { DOMAIN_PLEXES_INITIAL_QUERY_VARIABLES } from '../../../../graphql/queries/DomainPlexes';
import { colorPalette, spacings, TextSizesEnum } from '../../../../theme';
import { Loader } from '../../../shared/Loader';
import { IconButtonSize, PlexIconButton } from '../../../shared/primitives/PlexIconButton';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { Error } from '../../shared/Error';
import { DialogTopBar } from '../DialogTopBar';
import { useDialogState } from '../useDialogState';
import { PlexView } from './PlexView';

export const BrowsePlexesDialog = React.memo(
  (): React.ReactElement => {
    const classes = useStyles();
    const [, setDialogState] = useDialogState();
    const [searchQuery, setSearchQuery] = useState('');

    const { data, error, loading } = useDomainPlexesQuery({ variables: DOMAIN_PLEXES_INITIAL_QUERY_VARIABLES });

    if (loading) {
      return <Loader />;
    }

    if (error != null) {
      return <Error error={error} />;
    }

    const plexes =
      data?.currentUser?.domain?.plexes?.items.filter((p) => {
        if (p.deleted) {
          return false;
        }
        if (!searchQuery) {
          return true;
        }
        if (p.name.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1) {
          return true;
        }
        if (p.description && p.description?.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1) {
          return true;
        }
        return false;
      }) || [];

    return (
      <div className={classes.root}>
        <DialogTopBar>
          <div className={classes.searchRoot}>
            <SearchIconSvg className={classes.searchIcon} />
            <Input
              className={classes.searchInput}
              placeholder={'Search'}
              fullWidth={true}
              disableUnderline={true}
              onChange={(event: React.ChangeEvent<{ name?: string; value: unknown }>): void => {
                setSearchQuery(event.target.value as string);
              }}
            />
          </div>
        </DialogTopBar>

        <div className={classes.content}>
          <div className={classes.contentTitle}>
            <PlexTypography className={classes.contentTitleText} text={'All Plexes'} size={TextSizesEnum.XXXLarge} />
            <div>
              <PlexIconButton
                onClick={(): void => {
                  setDialogState({ type: 'new_plex' });
                }}
                MuiIcon={AddIcon}
                size={IconButtonSize.Normal}
              />
            </div>
          </div>
          <div className={classes.plexesList}>
            {plexes.map((p) => (
              <PlexView key={p.id} plex={p} />
            ))}
          </div>
        </div>
      </div>
    );
  }
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    },
    searchRoot: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      position: 'relative',
      marginLeft: spacings.xlarge,
      marginRight: spacings.xlarge,
      width: '100%',
    },
    searchIcon: {
      position: 'absolute',
    },
    searchInput: {
      display: 'flex',
      height: 60,
      paddingLeft: 40,
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflowY: 'auto',
    },
    contentTitle: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      height: 56,
      marginTop: spacings.normal,
      paddingLeft: spacings.xxlarge,
      paddingRight: spacings.xlarge,
    },
    contentTitleText: {
      color: colorPalette.dark._800,
      fontWeight: 'bold',
    },
    plexesList: {
      display: 'flex',
      flexWrap: 'wrap',
      margin: 20,
    },
  })
);
